import {
  IconButton,
  InputAdornment,
  TextField,
  useMediaQuery
} from "@mui/material";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

import { useState } from "react";

import { useTranslation } from "react-i18next";

import { DatePicker } from "../datePicker/DatePicker";
import {
  ArrowLeft20Px as ArrowLeftIcon,
  ArrowRight20Px as ArrowRightIcon
} from "../icons/autostoreIcons/icons20px";
import { mainColours } from "../theme/colours";
import { mobileWidth } from "../theme/theme";

dayjs.extend(advancedFormat);

type ASTableV2DatePickerProps = {
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
};

export const ASTableV2DatePicker = ({
  selectedDate,
  onDateChange
}: ASTableV2DatePickerProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(mobileWidth);
  const [datePickerIsOpen, setDatePickerIsOpen] = useState(false);

  const datePickerDisplayText = selectedDate
    ? dayjs(selectedDate).format("ddd, MMM Do")
    : t("select date");

  const handlePreviousDay = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (selectedDate) {
      onDateChange(dayjs(selectedDate).subtract(1, "day").toDate());
    }
  };

  const handleNextDay = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (selectedDate) {
      onDateChange(dayjs(selectedDate).add(1, "day").toDate());
    }
  };

  return (
    <>
      <TextField
        role="combobox"
        aria-label="Date filter for table"
        aria-expanded={datePickerIsOpen}
        aria-haspopup="dialog"
        value={datePickerDisplayText}
        variant="outlined"
        onClick={() => setDatePickerIsOpen(true)}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  onClick={handlePreviousDay}
                  aria-label="Previous day"
                  size="small"
                >
                  <ArrowLeftIcon
                    style={{
                      fill: mainColours.icon.secondary,
                      width: 18,
                      height: 18
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleNextDay}
                  aria-label="Next day"
                  size="small"
                >
                  <ArrowRightIcon
                    style={{
                      fill: mainColours.icon.secondary,
                      width: 18,
                      height: 18
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              textAlign: "center",
              fontSize: isMobile ? "0.875rem" : "1rem",
              px: 1,
              height: 40,
              borderRadius: "10px",
              backgroundColor: "background.paper",
              "& .MuiOutlinedInput-input": {
                cursor: "pointer",
                textAlign: "center"
              }
            }
          }
        }}
        sx={{
          width: "100%",
          maxWidth: 300
        }}
      />

      <DatePicker
        isOpen={datePickerIsOpen}
        setIsOpenCb={setDatePickerIsOpen}
        onDateChange={onDateChange}
        selectedDate={selectedDate}
      />
    </>
  );
};
